/**
 * Shifts as web/ reads and writes them: the wire row (ApiShift) and the delivered row (Shift),
 * the shift types, tasks, the clock and the work-time history.
 */
import { del, get, post, put } from '@/api/client';
import { createOnce } from '@/api/createOps';
import { useCan } from '@/auth/caps';
import { markStints } from '@/rules/stints';
import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { BreakMode } from '@timefly/core';
import type { ApiUser } from './people';

export interface ApiShiftType {
  id: number;
  name: string;
  color_code: string | null;
  start_time: string | null;
  end_time: string | null;
}
export interface ValidationError {
  code: string;
  text: string;
  severity: 'danger' | 'warning';
  type: 'daily' | 'weekly';
  hours?: number;
  minutes?: number;
  max?: number;
  /** Weekly entries only: 'Planned' when the plan breaks the rule, 'Actual' when the punches do. */
  label?: 'Planned' | 'Actual';
  /** year_over_200 only: the first day of the 52 weeks counted, YYYY-MM-DD (plan 0027 batch C). */
  from?: string;
  /** missing_skill only: the skills the shift type needs that the person lacks, by name (#283). */
  skills?: string;
}
/** A task on a shift, as both shift routes already return it. */
export interface ApiTaskAssignment {
  id: number;
  shift_id: number;
  shift_task_id: number;
  status: 'pending' | 'in-progress' | 'completed' | string;
  completed_at?: string | null;
  completed_by?: number | null;
  has_time_range?: boolean;
  start_time?: string | null;
  end_time?: string | null;
  /** «Må gjøres» (#202): late when not done by its end */
  required?: boolean;
  /** the steps on this shift (#202); null is none */
  steps?: Step[] | null;
  shiftTask: {
    id: number;
    name: string;
    description?: string | null;
    icon?: string | null;
    /** the activity's colour (#202), one of activityIcon COLORS; null is blue */
    color?: string | null;
    /** added «Bare denne gangen» (#202): not in the library */
    one_off?: boolean;
  } | null;
  completedByUser?: { id: number; name: string; lastname: string | null } | null;
}
/** One step of an activity on a shift (#202). */
export interface Step {
  text: string;
  done: boolean;
}
/** A shift row as the API sends it. Screens read `Shift`, what the shift hooks deliver. */
export interface ApiShift {
  id: number;
  /** the project the hours go under (2026-09-29); null is "uten prosjekt" */
  project_id?: number | null;
  user_id: number | null;
  shift_type_id: number | null;
  description: string | null;
  start_time: string;
  end_time: string;
  is_approved: boolean;
  punch_in_time: string | null;
  punch_out_time: string | null;
  lunch_in_time: string | null;
  lunch_out_time: string | null;
  /** planlagt lunsj (owner, 2026-10-08): a plan only, never read by pay; both or neither */
  planned_lunch_start?: string | null;
  planned_lunch_minutes?: number | null;
  /** the break mode the shift was punched in under, stamped by the database; null until punched (plan 0032) */
  break_mode?: BreakMode | null;
  published: boolean;
  is_swap_requested: boolean;
  is_offer_requested: boolean;
  shiftType: ApiShiftType | null;
  user: ApiUser | null;
  validation_errors?: ValidationError[];
  taskAssignments?: ApiTaskAssignment[];
}
/**
 * A shift as useShifts and useMyShifts deliver it: the wire row plus what markStints adds. A row
 * straight from a route (a write's answer, punch status) is a Shift with no stint.
 */
export type Shift = ApiShift & {
  /** set by markStints, not the API: the back-to-back stint this shift is part of, epoch ms; absent on a shift on its own */
  stint?: { start: number; end: number };
};
/**
 * Everything a change to shifts makes stale, decided here once (issue #76): the shift windows, the
 * employee's own shifts, the offers and swaps that carry shift rows, the pay report that reads
 * approved shifts, and the work-time history a write may add a row to (ADR 0021). The union of
 * what the scattered sites invalidated before; a site with a key of its own (punch status, the
 * task library, templates, the assistant's actions) invalidates it beside this. A correction closes a
 * Kontroll question on the server (#275), so the questions are read again too.
 */
export function invalidateShifts(qc: QueryClient) {
  return Promise.all(
    [['shifts'], ['my-shifts'], ['pending'], ['report'], ['work-time-changes'], ['clarifications']].map(
      (queryKey) => qc.invalidateQueries({ queryKey }),
    ),
  );
}

export function useShifts(start: string, end: string, enabled = true) {
  return useQuery({
    queryKey: ['shifts', start, end],
    enabled,
    queryFn: () =>
      get<{ shifts: ApiShift[]; publicationStatus: string }>(
        `/admin/shifts/date-range?start_date=${start}&end_date=${end}&include_validation=true`,
      ),
    // one automatic break per back-to-back stint, the way pay counts it (rules markStints)
    select: (d) => ({ ...d, shifts: markStints(d.shifts ?? []) }),
    staleTime: 30_000,
  });
}
/**
 * The same window without the server's rule check, about a third of its time at BK (RC 2026-10-28): what a
 * page draws while the checked read is on its way. Its key sits under ['shifts'], so every write refreshes it.
 */
export function useShiftsPlain(start: string, end: string, enabled = true) {
  return useQuery({
    queryKey: ['shifts', start, end, 'plain'],
    enabled,
    queryFn: () =>
      get<{ shifts: ApiShift[]; publicationStatus: string }>(
        `/admin/shifts/date-range?start_date=${start}&end_date=${end}`,
      ),
    select: (d) => ({ ...d, shifts: markStints(d.shifts ?? []) }),
    staleTime: 30_000,
  });
}
/**
 * The revision an approver was shown, for PUT /admin/shifts/:id/accept `if_unchanged` (TIME02D-06):
 * the server compares it with the row under its lock and refuses 409 precondition_failed, approving
 * nothing, when the times or the person moved since the read. An approval is of the values on screen.
 */
export const revisionOf = (s: ApiShift) => ({
  start_time: s.start_time,
  end_time: s.end_time,
  user_id: s.user_id ?? null,
  punch_in_time: s.punch_in_time ?? null,
  punch_out_time: s.punch_out_time ?? null,
  lunch_in_time: s.lunch_in_time ?? null,
  lunch_out_time: s.lunch_out_time ?? null,
  is_approved: !!s.is_approved,
});
/** PUT /admin/shifts/:id/accept refuses is_approved:true without both punches (400). With `shift`, bound to what it holds. */
export function useApprove() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, approved, shift }: { id: number; approved: boolean; shift?: ApiShift }) =>
      put<{ message: string }>(`/admin/shifts/${id}/accept`, {
        is_approved: approved,
        ...(shift ? { if_unchanged: revisionOf(shift) } : {}),
      }),
    onSettled: () => invalidateShifts(qc),
  });
}
/** The receipt POST /admin/report-receipts answers (#295 ONB-16): kept before the browser starts the download. */
export interface ReportReceipt {
  id: number;
  prepared_at: string;
  existing: boolean;
  records: number;
  people: number;
  fingerprint: string;
}
/** A report of exactly these approved records at the revision shown; a changed one is refused 409 report_changed. */
export function useReportReceipt() {
  return useMutation({
    mutationFn: (b: { from: string; to: string; scope_label: string; shifts: ApiShift[]; content: string }) =>
      post<ReportReceipt>('/admin/report-receipts', {
        format: 'approved_hours_csv',
        from: b.from,
        to: b.to,
        scope_label: b.scope_label,
        records: b.shifts.map((s) => ({ id: s.id, if_unchanged: revisionOf(s) })),
        content: b.content,
      }),
  });
}
export function useShiftTypes() {
  return useQuery({
    queryKey: ['shift-types'],
    queryFn: () => get<ApiShiftType[]>('/admin/shift-types'),
    staleTime: 5 * 60_000,
  });
}
/** The body POST and PUT /admin/shifts read (createShift, updateShift in admin/shiftController.js). */
export interface ShiftWrite {
  user_id: number | null;
  shift_type_id: number | null;
  description?: string | null;
  start_time: string;
  end_time: string;
  punch_in_time?: string | null;
  punch_out_time?: string | null;
  lunch_in_time?: string | null;
  lunch_out_time?: string | null;
  planned_lunch_start?: string | null;
  planned_lunch_minutes?: number | null;
  published?: boolean;
  is_approved?: boolean;
  /** omitted keeps the stored rows; sent, the server matches rows by id, updates them, deletes the missing and creates the id-less (TF-WEB-COMPLETION) */
  taskAssignments?: TaskWrite[];
  /** why registered time moves; the server requires it when a clock or break time that was set changes (work-time history, 2026-09-21) */
  change_reason?: string;
  /** the owner's override: move a clocked shift to another day, with change_reason (2026-09-27) */
  move_clocked?: boolean;
  /** a clock-in more than 12 h from the planned start, saved anyway with change_reason (#217) */
  clock_far_ok?: boolean;
  /** a create's operation id (issue #157): the same id and body sent again replays, never duplicates */
  op_id?: string;
}
/** One task row as createShift and updateShift validate it (backend/controllers/admin/shiftController.js, the taskAssignments validation). */
export interface TaskWrite {
  id?: number;
  /** one_off sent only where it is or was one (#202): true «Bare denne gangen», false «Lagre som aktivitet» */
  shiftTask: { name: string; description: string | null; one_off?: boolean };
  /** sent only where it is or was set (#202); omitted, the server keeps the stored value */
  required?: boolean;
  steps?: Step[] | null;
  status?: string;
  completedAt?: string | null;
  has_time_range?: boolean | undefined;
  start_time?: string | null | undefined;
  end_time?: string | null | undefined;
}
/** One hook for every shift write; each invalidates the shift windows. */
export function useShiftWrites() {
  const qc = useQueryClient();
  // every shift write can add a history row (ADR 0021), so the history is read again too
  const done = () => invalidateShifts(qc);
  const create = useMutation({
    // one operation per create, so a lost answer is resent, never made again (#157)
    mutationFn: (b: ShiftWrite) =>
      createOnce(qc.getQueryData<{ id: number }>(['tenant'])?.id, b, (sent) =>
        post<ApiShift>('/admin/shifts', sent),
      ),
    onSettled: done,
  });
  const update = useMutation({
    mutationFn: ({ id, body }: { id: number; body: Partial<ShiftWrite> }) =>
      put<ApiShift>(`/admin/shifts/${id}`, body),
    onSettled: done,
  });
  const remove = useMutation({
    // deleting registered time carries its reason (work-time history, 2026-09-21)
    mutationFn: (v: number | { id: number; reason?: string | undefined }) => {
      const { id, reason } = typeof v === 'number' ? { id: v, reason: undefined } : v;
      return del<{ message: string }>(
        `/admin/shifts/${id}${reason ? `?reason=${encodeURIComponent(reason)}` : ''}`,
      );
    },
    onSettled: done,
  });
  const publish = useMutation({
    mutationFn: (id: number) => put<unknown>(`/admin/shifts/${id}/publish`, {}),
    onSettled: done,
  });
  /** Publishes every unpublished shift in the window; refuses (400) when one overlaps a published shift. */
  const publishAll = useMutation({
    mutationFn: (w: { start_date: string; end_date: string }) =>
      put<unknown>('/admin/shifts/publish-shifts', w),
    onSettled: done,
  });
  /** Move one task to another shift; the server moves it in one transaction or not at all (V19 slice 4). */
  const moveTask = useMutation({
    mutationFn: ({ id, to }: { id: number; to: number }) =>
      post<unknown>(`/admin/shift-task-assignments/${id}/move`, { to_shift_id: to }),
    onSettled: done,
  });
  /**
   * Move the edge between two back-to-back shifts: the earlier one ends and the later one starts
   * at `at`, both or neither (V19 slice 4). The answer carries each shift's notice result.
   */
  const boundary = useMutation({
    mutationFn: (b: { earlier_id: number; later_id: number; at: string }) =>
      put<{ notification_status?: { earlier?: string; later?: string } }>('/admin/shifts/boundary', b),
    onSettled: done,
  });
  return { create, update, remove, publish, publishAll, moveTask, boundary };
}
/** One row of the work-time history (ADR 0021, backend/services/workTimeLog.js): the fields that moved as [old, new]. */
export interface ApiWorkTimeChange {
  id: number;
  shift_id: number | null;
  absence_id: number | null;
  user_id: number | null;
  changed_by: number | null;
  changed_by_name?: string | null;
  action: string;
  changes: Record<string, [unknown, unknown]>;
  reason: string | null;
  created_at: string;
}
/** GET /admin/work-time-changes (timesheet.view), or the caller's own with `mine`. */
export function useWorkTimeChanges(
  q: {
    shift_id?: number | undefined;
    user_id?: number | undefined;
    absence_id?: number | undefined;
    action?: string | undefined;
    from?: string | undefined;
    to?: string | undefined;
    mine?: boolean;
  },
  enabled = true,
) {
  const qs = new URLSearchParams(
    Object.entries({
      shift_id: q.shift_id,
      user_id: q.user_id,
      absence_id: q.absence_id,
      action: q.action,
      from: q.from,
      to: q.to,
    })
      .filter(([, v]) => v != null)
      .map(([k, v]) => [k, String(v)]),
  ).toString();
  return useQuery({
    queryKey: ['work-time-changes', q],
    queryFn: () =>
      get<ApiWorkTimeChange[]>(q.mine ? '/work-time-changes/mine' : `/admin/work-time-changes?${qs}`),
    enabled,
  });
}
export interface ShiftTypeWrite {
  name: string;
  color_code: string | null;
  start_time: string | null;
  end_time: string | null;
}
export function useShiftTypeWrites() {
  const qc = useQueryClient();
  const done = () => qc.invalidateQueries({ queryKey: ['shift-types'] });
  return {
    create: useMutation({
      mutationFn: (b: ShiftTypeWrite) => post<ApiShiftType>('/admin/shift-types', b),
      onSettled: done,
    }),
    update: useMutation({
      mutationFn: ({ id, body }: { id: number; body: Partial<ShiftTypeWrite> }) =>
        put<ApiShiftType>(`/admin/shift-types/${id}`, body),
      onSettled: done,
    }),
    remove: useMutation({
      mutationFn: (id: number) => del<unknown>(`/admin/shift-types/${id}`),
      onSettled: done,
    }),
  };
}
/**
 * The company's task library: every definition, retired ones included.
 *
 * Retired definitions are only listed here. The picker in the shift drawer asks the same route
 * without include_inactive and gets live ones only, which is right for a suggestion list and
 * wrong for the page that has to be able to bring one back.
 */
export interface ApiShiftTask {
  id: number;
  name: string;
  description: string | null;
  /** the manager's icon, null for automatic (owner 2026-10-18; backend services/activityIcons.js) */
  icon?: string | null;
  /** the colour on the timeline (#202), null is blue */
  color?: string | null;
  is_active: boolean;
  /** what a new task of this type starts with (#293): planning minutes, never paid time; and its proof */
  default_estimate_minutes?: number | null;
  requirement?: 'comment' | 'measurement' | null;
  /** how many shifts carry it, counted by the server; a string when pg returns bigint */
  in_use: number | string;
}
export function useShiftTaskLibrary() {
  const can = useCan();
  return useQuery({
    queryKey: ['shift-task-library'],
    queryFn: () => get<ApiShiftTask[]>('/admin/shift-tasks?include_inactive=1'),
    enabled: can('vaktplan.se'),
    staleTime: 60_000,
  });
}
export interface ShiftTaskWrite {
  name?: string;
  description?: string | null;
  is_active?: boolean;
  icon?: string | null;
  color?: string | null;
  /** false only: a one-off joins the library («Lagre som aktivitet», #202) */
  one_off?: false;
  default_estimate_minutes?: number | null;
  requirement?: 'comment' | 'measurement' | null;
}
/** A company's activity pack («Pakker», #202): activities placed one after another; minutes 0 is «Hele vakten». */
export interface ApiActivityPack {
  id: number;
  name: string;
  icon: string | null;
  color: string | null;
  items: { name: string; minutes: number }[];
}
export type ActivityPackWrite = Partial<Omit<ApiActivityPack, 'id'>>;
export function useActivityPacks() {
  const can = useCan();
  return useQuery({
    queryKey: ['activity-packs'],
    queryFn: () => get<ApiActivityPack[]>('/admin/activity-packs'),
    enabled: can('vaktplan.se'),
    staleTime: 60_000,
  });
}
export function useActivityPackWrites() {
  const qc = useQueryClient();
  const done = () => qc.invalidateQueries({ queryKey: ['activity-packs'] });
  return {
    create: useMutation({
      mutationFn: (body: ActivityPackWrite) => post<ApiActivityPack>('/admin/activity-packs', body),
      onSettled: done,
    }),
    update: useMutation({
      mutationFn: ({ id, body }: { id: number; body: ActivityPackWrite }) =>
        put<ApiActivityPack>(`/admin/activity-packs/${id}`, body),
      onSettled: done,
    }),
    remove: useMutation({
      mutationFn: (id: number) => del<{ id: number }>(`/admin/activity-packs/${id}`),
      onSettled: done,
    }),
  };
}
export function useShiftTaskWrites() {
  const qc = useQueryClient();
  // a rename reaches three places: this page, the picker in the shift drawer, and the task
  // names drawn on the shifts themselves, which arrive nested in the shift rows
  const done = () => {
    qc.invalidateQueries({ queryKey: ['shift-task-library'] });
    qc.invalidateQueries({ queryKey: ['shift-tasks'] });
    void invalidateShifts(qc);
  };
  return {
    create: useMutation({
      // a name alone, or with the icon, colour and description chosen in «Lagre som type» (#202)
      mutationFn: (b: string | { name: string; description?: string; icon?: string; color?: string }) =>
        post<ApiShiftTask>('/admin/shift-tasks', typeof b === 'string' ? { name: b } : b),
      onSettled: done,
    }),
    update: useMutation({
      // the answer is the surviving row: a rename onto a name the company already uses merges,
      // and says so with merged_from naming the row that went
      mutationFn: ({ id, body }: { id: number; body: ShiftTaskWrite }) =>
        put<ApiShiftTask & { merged_from?: number }>(`/admin/shift-tasks/${id}`, body),
      onSettled: done,
    }),
  };
}
export interface PunchStatus {
  isPunchedIn: boolean;
  isOnLunch: boolean;
  lastPunchedInTime: string | null;
  lastPunchedOutTime: string | null;
  lunchInTime: string | null;
  lunchOutTime: string | null;
  shift: ApiShift | null;
  nextShift: ApiShift | null;
}
export function usePunchStatus() {
  return useQuery({
    queryKey: ['punch-status'],
    queryFn: () => get<PunchStatus>('/shifts/isCurrentlyPunchedIn'),
    staleTime: 10_000,
  });
}
/** The four clock routes take no body; the person is the token (controllers/user/shiftController.js). */
export function usePunch() {
  const qc = useQueryClient();
  const done = () =>
    Promise.all([qc.invalidateQueries({ queryKey: ['punch-status'] }), invalidateShifts(qc)]);
  const mk = (path: string) =>
    // punch-in may carry the project the hours go under (2026-09-29)
    useMutation({
      // biome-ignore lint/suspicious/noConfusingVoidType: void lets punchOut() and the others be called with no argument
      mutationFn: (body: Record<string, unknown> | void) => post<unknown>(`/shifts/${path}`, body ?? {}),
      onSettled: done,
    });
  return {
    punchIn: mk('punch-in'),
    punchOut: mk('punch-out'),
    lunchIn: mk('lunch-in'),
    lunchOut: mk('lunch-out'),
    /** «Bytt prosjekt» (#115): clock out and in at the same second under the new project; the shift splits */
    switchProject: mk('switch-project'),
  };
}
/**
 * One task's status, saved on its own through the existing route
 * (`PUT /admin/shift-tasks/assignments/:id/status`, open to the shift's owner
 * and to a manager of the company). The shift itself is never resent, so no
 * other field of the shift can be touched by a status change.
 */
export function useTaskStatus() {
  return useMutation({
    mutationFn: ({ id, status }: { id: number; status: string }) =>
      put<ApiTaskAssignment & { notification?: string }>(`/admin/shift-tasks/assignments/${id}/status`, {
        status,
      }),
    // The refresh is the caller's, because the caller has to know whether it
    // actually succeeded before it lets anything be written again
    // (TF-TASK-RECOVERY). Invalidating here would hide that outcome.
  });
}
/** GET /shifts/date-range answers the caller's own shifts in the same shape as the admin route. */
export function useMyShifts(start: string, end: string) {
  return useQuery({
    queryKey: ['my-shifts', start, end],
    queryFn: () =>
      get<{ shifts: ApiShift[]; publicationStatus: string }>(
        `/shifts/date-range?start_date=${start}&end_date=${end}`,
      ),
    // one automatic break per back-to-back stint, the way pay counts it (rules markStints)
    select: (d) => ({ ...d, shifts: markStints(d.shifts ?? []) }),
    staleTime: 30_000,
  });
}
